/**
 * Tool Call Card
 *
 * Display tool execution with confirmation UI for pending actions
 */

import { CheckCircle, XCircle, AlertCircle, Clock, Ban } from 'lucide-react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface ToolCall {
  toolName: string;
  input: any;
  output?: any;
  error?: string;
  status: 'success' | 'error' | 'pending_confirmation' | 'cancelled';
  requiresConfirmation: boolean;
}

interface ToolCallCardProps {
  toolCall: ToolCall;
  onConfirm: (confirmed: boolean) => void;
}

export function ToolCallCard({ toolCall, onConfirm }: ToolCallCardProps) {
  const { t } = useTranslation();
  const { toolName, input, output, error, status} = toolCall;

  // Status icon
  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'pending_confirmation':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'cancelled':
        return <Ban className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-blue-500" />;
    }
  };

  // Status badge
  const getStatusBadge = () => {
    switch (status) {
      case 'success':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{t('assistant.tool_status.executed')}</Badge>;
      case 'error':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{t('assistant.tool_status.error')}</Badge>;
      case 'pending_confirmation':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">{t('assistant.tool_status.requires_confirmation')}</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">{t('assistant.tool_status.cancelled')}</Badge>;
      default:
        return <Badge variant="outline">{t('assistant.tool_status.processing')}</Badge>;
    }
  };

  // Get human-readable tool name
  const getToolDisplayName = (name: string) => {
    const names: Record<string, string> = {
      'inventory_findDevice': t('assistant.tool_names.inventory_findDevice'),
      'inventory_getDevice': t('assistant.tool_names.inventory_getDevice'),
      'inventory_searchDevices': t('assistant.tool_names.inventory_searchDevices'),
      'inventory_assignDevice': t('assistant.tool_names.inventory_assignDevice'),
      'inventory_unassignDevice': t('assistant.tool_names.inventory_unassignDevice'),
      'inventory_updateDeviceFields': t('assistant.tool_names.inventory_updateDeviceFields'),
      'approvals_listPending': t('assistant.tool_names.approvals_listPending'),
      'approvals_getRequest': t('assistant.tool_names.approvals_getRequest'),
      'approvals_createFromAlta': t('assistant.tool_names.approvals_createFromAlta'),
      'approvals_decide': t('assistant.tool_names.approvals_decide'),
      'users_getAlta': t('assistant.tool_names.users_getAlta'),
      'users_searchAltas': t('assistant.tool_names.users_searchAltas'),
      'users_getBaja': t('assistant.tool_names.users_getBaja'),
      'users_searchBajas': t('assistant.tool_names.users_searchBajas'),
      'users_searchCommunity': t('assistant.tool_names.users_searchCommunity'),
      'notifications_createInApp': t('assistant.tool_names.notifications_createInApp'),
      'notifications_sendToRole': t('assistant.tool_names.notifications_sendToRole'),
    };
    return names[name] || name;
  };

  return (
    <Card className={cn(
      'border-l-4',
      status === 'success' && 'border-l-green-500',
      status === 'error' && 'border-l-red-500',
      status === 'pending_confirmation' && 'border-l-yellow-500',
      status === 'cancelled' && 'border-l-gray-400'
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <h4 className="text-sm font-medium">{getToolDisplayName(toolName)}</h4>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Input parameters */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">{t('assistant.params_label')}</p>
          <div className="bg-muted p-2 rounded text-xs">
            <pre className="whitespace-pre-wrap font-mono">
              {JSON.stringify(
                // Hide idempotencyKey from display
                Object.fromEntries(
                  Object.entries(input).filter(([key]) => key !== 'idempotencyKey')
                ),
                null,
                2
              )}
            </pre>
          </div>
        </div>

        {/* Output (if success) */}
        {output && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">{t('assistant.result_label')}</p>
            <div className="bg-green-50 p-2 rounded text-xs">
              <pre className="whitespace-pre-wrap font-mono text-green-900">
                {JSON.stringify(output, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Error (if failed) */}
        {error && (
          <div>
            <p className="text-xs font-medium text-red-600 mb-1">{t('assistant.error_label')}</p>
            <div className="bg-red-50 p-2 rounded text-xs text-red-900">
              {error}
            </div>
          </div>
        )}

        {/* Confirmation buttons */}
        {status === 'pending_confirmation' && (
          <div className="flex gap-2 pt-2">
            <Button
              onClick={() => onConfirm(true)}
              size="sm"
              className="flex-1"
            >
              {t('assistant.confirm_btn')}
            </Button>
            <Button
              onClick={() => onConfirm(false)}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              {t('assistant.cancel_btn')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
